import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { DealData } from "@/pages/deals/types";
import { cn } from "@/utils/utils";
import { LeadData, LeadOpenStatuses, LeadStatus } from "@/pages/leads/types";
import { CompanyData } from "@/pages/companies/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { Add } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import { useUsers } from "@/entities/users/hooks";
import { dealOpenStatuses } from "@/pages/deals/helpers";
import TextEditorWithActions from "@/components/controls/TextEditor/TextEditorWithActions";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import {
  useCreateNextStepMutation,
  useDealNextSteps,
  useDeleteNextStepMutation,
  useUpdateNextStepMutation
} from "@/pages/deals/hooks";
import toast from "react-hot-toast";
import { TokenHolderCard } from "@/components/layouts/NextSteps/TokenHolderCard";
import { TaskModalState } from "@/entities/tasks/types";
import { createModalStateSetter } from "@/entities/modals/helpers";
import TaskModal from "@/components/layouts/TasksDrawer/TaskModal";
import OpenTasks from "@/components/layouts/NextSteps/OpenTasks";
import IconContainer from "@/components/ui/icon-container";
import { GradientCardContainer } from "@/components/layouts/NextSteps/GradientCardContainer";
import NextStepItem from "@/components/controls/NextStepItem";
import { find, isEmpty, isNull, isUndefined, map, orderBy } from "lodash";
import { PersonData } from "@/entities/people/types";
import { PartnerData } from "@/pages/partners/types";
import { ContractData } from "@/pages/contracts/types";
import UpcomingActivities from "@/components/layouts/NextSteps/UpcomingActivities";
import { useActiveFieldKeys } from "@/pages/settings/hooks";

interface Props {
  entity:
    | DealData
    | LeadData
    | PersonData
    | CompanyData
    | PartnerData
    | ContractData
    | undefined;
  entityType:
    | EntityType.DEAL
    | EntityType.LEAD
    | EntityType.PERSON
    | EntityType.COMPANY
    | EntityType.PARTNER
    | EntityType.CONTRACT;
  className?: string;
  isQuickView?: boolean;
  toggleTasksDrawer?: () => void;
}

export interface DeleteNextStepModalState {
  isOpen: boolean;
  nextStepId: string | null;
}

export const SECTION_CLASSNAME =
  "pb-6 border-b border-border-border1 first:pt-0 last:border-none last:pb-0";

const NextSteps: React.FC<Props> = ({
  entity,
  entityType,
  className,
  isQuickView = false,
  toggleTasksDrawer
}) => {
  const { t } = useTranslation();
  const { activeFieldKeys, isLoaded: fieldsLoaded } =
    useActiveFieldKeys(entityType);
  const showLeadNextSteps =
    entityType === EntityType.LEAD &&
    (!fieldsLoaded || activeFieldKeys.has("tokenHolder")) &&
    LeadOpenStatuses.includes(entity?.status as LeadStatus);
  const [isAddingNewStep, setIsAddingNewStep] = useState(false);

  const isAddNewDisabled =
    entityType === EntityType.DEAL &&
    !find(dealOpenStatuses, (status) => status === entity?.status);

  const { isDropdownClosing } = useDropdownClosing();

  const tokenHolderId = showLeadNextSteps
    ? (entity as LeadData)?.tokenHolder
    : undefined;
  const userIds = useMemo(
    () => (tokenHolderId ? [tokenHolderId] : []),
    [tokenHolderId]
  );
  const {
    users: [tokenHolder]
  } = useUsers(userIds);

  const { data: nextStepsData, refetch } = useDealNextSteps(
    entity?.id || "",
    entityType === EntityType.DEAL && !isUndefined(entity)
  );

  const allNextSteps = nextStepsData?.nextSteps || [];
  const nextSteps = allNextSteps.filter((step) => !step.isDeleted).slice(0, 1);

  const createNextStep = useCreateNextStepMutation();
  const updateNextStep = useUpdateNextStepMutation();
  const deleteNextStep = useDeleteNextStepMutation();

  const [taskModalState, setTaskModalState] = useState<TaskModalState>({
    isOpen: false,
    isEditMode: false,
    editTaskId: null
  });

  const setIsTaskModalOpen = createModalStateSetter(setTaskModalState);

  const [deleteNextStepModalState, setDeleteNextStepModalState] =
    useState<DeleteNextStepModalState>({
      isOpen: false,
      nextStepId: null
    });

  const handleCreateNewStep = (value: string) => {
    if (entityType !== EntityType.DEAL) return null;
    const trimmedValue = value.trim();
    if (isEmpty(trimmedValue)) return null;
    createNextStep.mutate(
      {
        pathParams: { dealId: entity?.id || "" },
        data: {
          description: trimmedValue
        }
      },
      {
        onSuccess: () => {
          refetch();
          setIsAddingNewStep(false);
        }
      }
    );
  };

  const handleAddNewClick = () => {
    if (!isAddNewDisabled) {
      setIsAddingNewStep(true);
    }
  };

  const handleCancelAddNew = () => {
    setIsAddingNewStep(false);
  };

  const handleUpdateStep = async (nextStepId: string, newText?: string) => {
    if (entityType !== EntityType.DEAL) return;
    let trimmedValue = newText;
    if (!isUndefined(newText)) {
      trimmedValue = newText?.trim();
      if (isEmpty(trimmedValue)) {
        setDeleteNextStepModalState({
          isOpen: true,
          nextStepId: nextStepId
        });
        return;
      }
    }
    updateNextStep.mutate(
      {
        pathParams: { dealId: entity?.id || "", nextStepId },
        data: {
          ...(!isUndefined(newText) && { description: trimmedValue })
        }
      },
      {
        onSuccess: () => refetch()
      }
    );
  };

  const handleDeleteStep = () => {
    if (entityType !== EntityType.DEAL) return;
    if (isNull(deleteNextStepModalState.nextStepId)) return;
    deleteNextStep.mutate(
      {
        pathParams: {
          dealId: entity?.id || "",
          nextStepId: deleteNextStepModalState.nextStepId
        }
      },
      {
        onSuccess: () => {
          setDeleteNextStepModalState({
            isOpen: false,
            nextStepId: null
          });
          refetch();
          toast.success(t("deals.nextSteps.stepDeleted"), {
            duration: 5000,
            icon: null
          });
        }
      }
    );
  };

  const setIsDeleteNextStepModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  > = (value) => {
    setDeleteNextStepModalState((prevState) => ({
      ...prevState,
      isOpen: typeof value === "function" ? value(prevState.isOpen) : value
    }));
  };

  const hasNextSteps = nextSteps && nextSteps.length > 0;

  const handleNextStepDelete = (nextStepId: string) => {
    setDeleteNextStepModalState({
      isOpen: true,
      nextStepId: nextStepId
    });
  };

  // Show next steps section only for Deal and Lead entities (not Company or Person)
  const showNextSteps = entityType === EntityType.DEAL || showLeadNextSteps;

  return (
    <div
      className={cn(
        "flex flex-col gap-6 border-[0.5px] border-border-border1 w-full max-w-full rounded-xl",
        isQuickView ? "bg-surface-elevation2 p-4" : "bg-surface-elevation1 p-6",
        className
      )}
      data-test={`${entityType}-next-steps`}
    >
      <div
        className={cn(
          SECTION_CLASSNAME,
          "flex flex-col gap-4",
          showNextSteps ? "" : "!border-0 !pb-0"
        )}
        data-test={`${entityType}-next-steps-section`}
      >
        <Text variant={isQuickView ? "lgLabel" : "lgSubtitleH1"}>
          {t("deals.nextSteps.title")}
        </Text>
        {showNextSteps &&
          (entityType === EntityType.LEAD ? (
            showLeadNextSteps ? (
              <TokenHolderCard
                tokenHolder={tokenHolder}
                nextSteps={(entity as LeadData)?.tokenHolderNextSteps}
              />
            ) : null
          ) : (
            <GradientCardContainer>
              <div className="flex flex-col w-full">
                {!hasNextSteps && !isAddingNewStep && (
                  <Button
                    variant="tertiary"
                    disabled={isAddNewDisabled}
                    className="flex gap-1 w-fit shadow-none"
                    onClick={handleAddNewClick}
                    size="lg"
                  >
                    <IconContainer size="5" color="cta">
                      <Add />
                    </IconContainer>
                    <Text variant="body" className="text-cta">
                      {t("deals.nextSteps.addNew")}
                    </Text>
                  </Button>
                )}
                {!hasNextSteps && isAddingNewStep && (
                  <TextEditorWithActions
                    onSave={handleCreateNewStep}
                    onCancel={handleCancelAddNew}
                    width="100%"
                    clearOnSave
                    disabled={isAddNewDisabled}
                    className="!w-full !block"
                    showActions={true}
                    autoSave={false}
                    startInEditMode={true}
                    placeholder="Add the next step for this deal"
                    initialValue=""
                  />
                )}
                <div>
                  {map(
                    orderBy(nextSteps, ["createdAt"], ["desc"]),
                    (nextStep) => (
                      <NextStepItem
                        key={nextStep.id}
                        nextStep={nextStep}
                        allNextSteps={allNextSteps}
                        onUpdate={handleUpdateStep}
                        onDelete={handleNextStepDelete}
                      />
                    )
                  )}
                </div>
              </div>
            </GradientCardContainer>
          ))}
      </div>

      <UpcomingActivities
        entity={entity}
        entityType={entityType}
        isQuickView={isQuickView}
      />

      <div className="h-px bg-border-border1 shrink-0" />

      <OpenTasks
        entity={entity}
        setTaskModalState={setTaskModalState}
        isQuickView={isQuickView}
        toggleTasksDrawer={toggleTasksDrawer}
      />
      <ConfirmationModal
        isOpen={deleteNextStepModalState.isOpen && !isDropdownClosing}
        setIsOpen={setIsDeleteNextStepModalOpen}
        title={t("deals.nextSteps.deletionModal.title")}
        confirmationText={t("deals.nextSteps.deletionModal.confirmation")}
        handleSubmit={handleDeleteStep}
      />
      {taskModalState.isOpen && (
        <TaskModal
          isOpen={taskModalState.isOpen}
          setIsOpen={setIsTaskModalOpen}
          entityData={entity}
          entityType={entityType}
          isEditMode={taskModalState.isEditMode}
          editTaskId={taskModalState.editTaskId}
        />
      )}
    </div>
  );
};

export default NextSteps;
